import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Loader2, Trash2, Paperclip, Camera, Upload, FileText, Image as ImageIcon, X, Pencil } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { canEditEntry } from '@/lib/permissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0);

export default function ProjectBudget({ projectId, workspaceId }) {
    const { user, profile, session } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [lines, setLines] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openLine, setOpenLine] = useState(false);
    const [openExp, setOpenExp] = useState(false);
    const [editingLineId, setEditingLineId] = useState(null);
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [lineForm, setLineForm] = useState({ category: '', label: '', amount_planned: '', currency: 'XOF', note: '' });
    const [expForm, setExpForm] = useState({ label: '', amount: '', vendor: '', budget_line_id: '', note: '', date: '' });

    // Justificatifs (attachments) state
    const [attDialogExpenseId, setAttDialogExpenseId] = useState(null);
    const [attachmentsByExpense, setAttachmentsByExpense] = useState({});
    const [attCountByExpense, setAttCountByExpense] = useState({});
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [uploadingAtt, setUploadingAtt] = useState(false);

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const loadAttachmentCounts = useCallback(async (expenseIds) => {
        if (!expenseIds.length) return;
        const { data } = await supabase
            .from('expense_attachments')
            .select('expense_id')
            .in('expense_id', expenseIds);
        const counts = {};
        (data || []).forEach(a => { counts[a.expense_id] = (counts[a.expense_id] || 0) + 1; });
        setAttCountByExpense(counts);
    }, []);

    const load = async () => {
        setLoading(true);
        const [{ data: bl }, { data: ex }] = await Promise.all([
            supabase.from('budget_lines').select('*').eq('project_id', projectId).order('created_at'),
            supabase.from('expenses').select('*').eq('project_id', projectId).order('created_at', { ascending: false }),
        ]);
        setLines(bl || []); setExpenses(ex || []);
        setLoading(false);
        loadAttachmentCounts((ex || []).map(e => e.id));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const openAttachments = async (expenseId) => {
        setAttDialogExpenseId(expenseId);
        try {
            const r = await axios.get(`${API}/expenses/${expenseId}/attachments`, authHeader());
            setAttachmentsByExpense(prev => ({ ...prev, [expenseId]: r.data.items || [] }));
        } catch (e) {
            toast.error('Chargement des justificatifs échoué');
        }
    };

    const uploadAttachment = async (file) => {
        if (!attDialogExpenseId || !file) return;
        setUploadingAtt(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('label', file.name);
            const r = await axios.post(
                `${API}/expenses/${attDialogExpenseId}/attachments`,
                fd,
                { headers: { Authorization: `Bearer ${session?.access_token}` } },
            );
            const att = r.data.attachment;
            // Refresh to obtain signed URL
            const list = await axios.get(`${API}/expenses/${attDialogExpenseId}/attachments`, authHeader());
            setAttachmentsByExpense(prev => ({ ...prev, [attDialogExpenseId]: list.data.items || [] }));
            setAttCountByExpense(c => ({ ...c, [attDialogExpenseId]: (c[attDialogExpenseId] || 0) + 1 }));
            toast.success('Justificatif ajouté');
            return att;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload échoué');
        } finally {
            setUploadingAtt(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (cameraInputRef.current) cameraInputRef.current.value = '';
        }
    };

    const deleteAttachment = async (attId) => {
        if (!attDialogExpenseId) return;
        if (!window.confirm('Supprimer ce justificatif ?')) return;
        try {
            await axios.delete(`${API}/expenses/${attDialogExpenseId}/attachments/${attId}`, authHeader());
            setAttachmentsByExpense(prev => ({
                ...prev,
                [attDialogExpenseId]: (prev[attDialogExpenseId] || []).filter(a => a.id !== attId),
            }));
            setAttCountByExpense(c => ({ ...c, [attDialogExpenseId]: Math.max(0, (c[attDialogExpenseId] || 1) - 1) }));
        } catch (e) {
            toast.error('Suppression refusée');
        }
    };

    const resetLineForm = () => {
        setEditingLineId(null);
        setLineForm({ category: '', label: '', amount_planned: '', currency: 'XOF', note: '' });
    };
    const resetExpForm = () => {
        setEditingExpenseId(null);
        setExpForm({ label: '', amount: '', vendor: '', budget_line_id: '', note: '', date: '' });
    };

    const openEditLine = (l) => {
        setEditingLineId(l.id);
        setLineForm({
            category: l.category || '',
            label: l.label || '',
            amount_planned: l.amount_planned ?? '',
            currency: l.currency || 'XOF',
            note: l.note || '',
        });
        setOpenLine(true);
    };
    const openEditExpense = (e) => {
        setEditingExpenseId(e.id);
        setExpForm({
            label: e.label || '',
            amount: e.amount ?? '',
            vendor: e.vendor || '',
            budget_line_id: e.budget_line_id || '',
            note: e.note || '',
            date: e.date || '',
        });
        setOpenExp(true);
    };

    const submitLine = async (e) => {
        e.preventDefault();
        const base = {
            category: lineForm.category.trim(),
            label: lineForm.label.trim(),
            amount_planned: Number(lineForm.amount_planned || 0),
            currency: lineForm.currency || 'XOF',
            note: lineForm.note ? lineForm.note.trim() : null,
        };
        if (editingLineId) {
            const { error } = await supabase.from('budget_lines').update(base).eq('id', editingLineId);
            if (error) { toast.error(`Modification échouée : ${error.message || error.code}`); return; }
            try { await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'budget_line.updated', entity_type: 'budget_line', entity_id: editingLineId, new_value: base }); } catch { /* non-blocking */ }
            toast.success('Ligne modifiée');
        } else {
            const payload = { ...base, workspace_id: workspaceId, project_id: projectId, created_by: user?.id };
            const { error } = await supabase.from('budget_lines').insert(payload);
            if (error) { toast.error(`Création échouée : ${error.message || error.code}`); return; }
            try { await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'budget_line.created', entity_type: 'budget_line', new_value: payload }); } catch { /* non-blocking */ }
            toast.success('Ligne créée');
        }
        setOpenLine(false); resetLineForm(); load();
    };

    const submitExpense = async (e) => {
        e.preventDefault();
        const base = {
            label: expForm.label.trim(),
            amount: Number(expForm.amount || 0),
            vendor: expForm.vendor || null,
            budget_line_id: expForm.budget_line_id || null,
            note: expForm.note ? expForm.note.trim() : null,
        };
        if (expForm.date) base.date = expForm.date;
        if (editingExpenseId) {
            const { error } = await supabase.from('expenses').update(base).eq('id', editingExpenseId);
            if (error) { toast.error(`Modification échouée : ${error.message || error.code}`); return; }
            try { await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'expense.updated', entity_type: 'expense', entity_id: editingExpenseId, new_value: base }); } catch { /* non-blocking */ }
            toast.success('Dépense modifiée');
        } else {
            const payload = { ...base, workspace_id: workspaceId, project_id: projectId, created_by: user?.id };
            const { error } = await supabase.from('expenses').insert(payload);
            if (error) { toast.error(`Création échouée : ${error.message || error.code}`); return; }
            try { await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'expense.created', entity_type: 'expense', new_value: payload }); } catch { /* non-blocking */ }
            toast.success('Dépense créée');
        }
        setOpenExp(false); resetExpForm(); load();
    };

    const totalPlanned = lines.reduce((s, l) => s + Number(l.amount_planned || 0), 0);
    const totalSpent = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

    const removeLine = async (l) => {
        if (!window.confirm(`Supprimer la ligne « ${l.label} » ?`)) return;
        const { error } = await supabase.from('budget_lines').delete().eq('id', l.id);
        if (error) { toast.error("Vous n'êtes pas autorisé·e à supprimer cette ligne."); return; }
        await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'budget_line.deleted', entity_type: 'budget_line', entity_id: l.id, old_value: { label: l.label } });
        toast.success('Ligne supprimée');
        load();
    };

    const removeExpense = async (e) => {
        if (!window.confirm(`Supprimer la dépense « ${e.label} » ?`)) return;
        const { error } = await supabase.from('expenses').delete().eq('id', e.id);
        if (error) { toast.error("Vous n'êtes pas autorisé·e à supprimer cette dépense."); return; }
        await supabase.from('audit_logs').insert({ workspace_id: workspaceId, user_id: user?.id, action: 'expense.deleted', entity_type: 'expense', entity_id: e.id, old_value: { label: e.label } });
        toast.success('Dépense supprimée');
        load();
    };

    if (loading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>;

    return (
        <div className="space-y-6">
            <div className="grid sm:grid-cols-3 gap-5">
                <div className="card-soft p-6"><div className="eyebrow">Planifié</div><div className="font-heading text-3xl mt-2">{fmt(totalPlanned)}</div></div>
                <div className="card-soft p-6"><div className="eyebrow">Dépensé</div><div className="font-heading text-3xl mt-2">{fmt(totalSpent)}</div></div>
                <div className="card-soft p-6"><div className="eyebrow">Reste</div><div className="font-heading text-3xl mt-2">{fmt(totalPlanned - totalSpent)}</div></div>
            </div>

            <div className="card-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                    <h3 className="font-heading text-xl">Lignes budgétaires</h3>
                    <Button onClick={() => { resetLineForm(); setOpenLine(true); }} size="sm" className="bg-primary hover:bg-primary-hover" data-testid="add-budget-line"><Plus size={14} className="mr-1.5" /> Ligne</Button>
                </div>
                <table className="w-full">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-6 py-3">Catégorie</th><th className="px-6 py-3">Libellé</th><th className="px-6 py-3 text-right">Planifié</th><th className="px-6 py-3 text-right">Dépensé</th><th className="px-6 py-3 w-12"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {lines.length === 0 && <tr><td colSpan={5} className="px-6 py-8 text-center text-foreground/60">Aucune ligne budgétaire.</td></tr>}
                        {lines.map(l => {
                            const spent = expenses.filter(e => e.budget_line_id === l.id).reduce((s, e) => s + Number(e.amount || 0), 0);
                            const editable = canEditEntry({ entry: l, userId: user?.id, profile, currentWorkspace });
                            return (
                                <tr key={l.id} className="group" data-testid={`budget-line-${l.id}`}>
                                    <td className="px-6 py-3 text-foreground/80">{l.category}</td>
                                    <td className="px-6 py-3 font-medium">
                                        {l.label}
                                        {l.note && (
                                            <div className="text-xs text-muted-foreground mt-0.5 italic line-clamp-1" title={l.note}>
                                                💬 {l.note}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-right font-mono">{fmt(l.amount_planned)} {l.currency}</td>
                                    <td className="px-6 py-3 text-right font-mono text-primary">{fmt(spent)} {l.currency}</td>
                                    <td className="px-2 py-3 text-right">
                                        {editable && (
                                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button size="sm" variant="ghost" onClick={() => openEditLine(l)} data-testid={`budget-line-edit-${l.id}`}><Pencil size={13} /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => removeLine(l)} data-testid={`budget-line-delete-${l.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="card-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                    <h3 className="font-heading text-xl">Dépenses</h3>
                    <Button onClick={() => { resetExpForm(); setOpenExp(true); }} size="sm" className="bg-primary hover:bg-primary-hover" data-testid="add-expense"><Plus size={14} className="mr-1.5" /> Dépense</Button>
                </div>
                <table className="w-full">
                    <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-6 py-3">Libellé</th><th className="px-6 py-3">Fournisseur</th><th className="px-6 py-3 text-right">Montant</th><th className="px-6 py-3">Date</th><th className="px-3 py-3 text-center">Justifs</th><th className="px-6 py-3 w-12"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {expenses.length === 0 && <tr><td colSpan={6} className="px-6 py-8 text-center text-foreground/60">Aucune dépense.</td></tr>}
                        {expenses.map(e => {
                            const editable = canEditEntry({ entry: e, userId: user?.id, profile, currentWorkspace });
                            const attCount = attCountByExpense[e.id] || 0;
                            return (
                                <tr key={e.id} className="group" data-testid={`expense-${e.id}`}>
                                    <td className="px-6 py-3 font-medium">
                                        {e.label}
                                        {e.note && (
                                            <div className="text-xs text-muted-foreground mt-0.5 italic line-clamp-1" title={e.note}>
                                                💬 {e.note}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-foreground/80">{e.vendor || '—'}</td>
                                    <td className="px-6 py-3 text-right font-mono">{fmt(e.amount)} {e.currency}</td>
                                    <td className="px-6 py-3 text-sm">{e.date}</td>
                                    <td className="px-3 py-3 text-center">
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => openAttachments(e.id)}
                                            className="relative"
                                            data-testid={`expense-attachments-${e.id}`}
                                            title="Pièces justificatives"
                                        >
                                            <Paperclip size={13} />
                                            {attCount > 0 && (
                                                <span className="absolute -top-1 -right-1 h-3.5 min-w-3.5 px-0.5 text-[9px] font-semibold rounded-full bg-secondary text-white flex items-center justify-center">
                                                    {attCount}
                                                </span>
                                            )}
                                        </Button>
                                    </td>
                                    <td className="px-2 py-3 text-right">
                                        {editable && (
                                            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button size="sm" variant="ghost" onClick={() => openEditExpense(e)} data-testid={`expense-edit-${e.id}`}><Pencil size={13} /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => removeExpense(e)} data-testid={`expense-delete-${e.id}`}><Trash2 size={13} className="text-destructive" /></Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Budget line dialog */}
            <Dialog open={openLine} onOpenChange={(v) => { setOpenLine(v); if (!v) resetLineForm(); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{editingLineId ? 'Modifier la ligne' : 'Nouvelle ligne budgétaire'}</DialogTitle></DialogHeader>
                    <form onSubmit={submitLine} className="space-y-4" data-testid="budget-line-form">
                        <div className="space-y-2"><Label>Catégorie</Label><Input required value={lineForm.category} onChange={(e) => setLineForm({ ...lineForm, category: e.target.value })} placeholder="Études, Travaux, Équipement…" /></div>
                        <div className="space-y-2"><Label>Libellé</Label><Input required value={lineForm.label} onChange={(e) => setLineForm({ ...lineForm, label: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Montant</Label><Input type="number" required value={lineForm.amount_planned} onChange={(e) => setLineForm({ ...lineForm, amount_planned: e.target.value })} data-testid="budget-line-amount" /></div>
                            <div className="space-y-2"><Label>Devise</Label>
                                <select value={lineForm.currency} onChange={(e) => setLineForm({ ...lineForm, currency: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                                    <option>XOF</option><option>XAF</option><option>EUR</option><option>USD</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Commentaire / note (facultatif)</Label>
                            <Textarea
                                rows={3}
                                value={lineForm.note}
                                onChange={(e) => setLineForm({ ...lineForm, note: e.target.value })}
                                placeholder="Hypothèses de calcul, conditions, ajustement attendu…"
                                data-testid="budget-line-note"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpenLine(false)}>Annuler</Button>
                            <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="budget-line-submit">Enregistrer</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Expense dialog */}
            <Dialog open={openExp} onOpenChange={(v) => { setOpenExp(v); if (!v) resetExpForm(); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{editingExpenseId ? 'Modifier la dépense' : 'Nouvelle dépense'}</DialogTitle></DialogHeader>
                    <form onSubmit={submitExpense} className="space-y-4" data-testid="expense-form">
                        <div className="space-y-2"><Label>Libellé</Label><Input required value={expForm.label} onChange={(e) => setExpForm({ ...expForm, label: e.target.value })} data-testid="expense-label" /></div>
                        <div className="space-y-2"><Label>Fournisseur</Label><Input value={expForm.vendor} onChange={(e) => setExpForm({ ...expForm, vendor: e.target.value })} data-testid="expense-vendor" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Montant</Label><Input type="number" required value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount" /></div>
                            <div className="space-y-2"><Label>Date</Label><Input type="date" value={expForm.date || ''} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} data-testid="expense-date" /></div>
                        </div>
                        <div className="space-y-2">
                            <Label>Ligne budgétaire</Label>
                            <select value={expForm.budget_line_id} onChange={(e) => setExpForm({ ...expForm, budget_line_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="expense-line-select">
                                <option value="">—</option>
                                {lines.map(l => <option key={l.id} value={l.id}>{l.category} — {l.label}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Commentaire / note (facultatif)</Label>
                            <Textarea
                                rows={3}
                                value={expForm.note}
                                onChange={(e) => setExpForm({ ...expForm, note: e.target.value })}
                                placeholder="Contexte, validation orale, conditions de remboursement…"
                                data-testid="expense-note"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpenExp(false)}>Annuler</Button>
                            <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="expense-submit">Enregistrer</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            {/* Justificatifs / Attachments dialog */}
            <Dialog open={!!attDialogExpenseId} onOpenChange={(o) => !o && setAttDialogExpenseId(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Pièces justificatives</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4" data-testid="expense-attachments-dialog">
                        <p className="text-sm text-muted-foreground">
                            Importez un PDF / image ou prenez une photo directement depuis votre téléphone.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,application/pdf"
                                className="sr-only"
                                onChange={(e) => uploadAttachment(e.target.files?.[0])}
                                data-testid="attachment-file-input"
                            />
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="sr-only"
                                onChange={(e) => uploadAttachment(e.target.files?.[0])}
                                data-testid="attachment-camera-input"
                            />
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingAtt}
                                variant="outline"
                                size="sm"
                                data-testid="attachment-upload-btn"
                            >
                                <Upload size={14} className="mr-2" /> Importer
                            </Button>
                            <Button
                                onClick={() => cameraInputRef.current?.click()}
                                disabled={uploadingAtt}
                                className="bg-secondary hover:bg-secondary/90 text-white"
                                size="sm"
                                data-testid="attachment-camera-btn"
                            >
                                <Camera size={14} className="mr-2" /> Prendre une photo
                            </Button>
                            {uploadingAtt && <span className="text-xs text-muted-foreground inline-flex items-center gap-1 self-center"><Loader2 size={12} className="animate-spin" /> Téléversement…</span>}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                            {(attachmentsByExpense[attDialogExpenseId] || []).length === 0 && (
                                <div className="col-span-full text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-lg" data-testid="attachment-empty">
                                    Aucun justificatif pour cette dépense.
                                </div>
                            )}
                            {(attachmentsByExpense[attDialogExpenseId] || []).map(att => {
                                const isImage = (att.mime_type || '').startsWith('image/');
                                return (
                                    <div
                                        key={att.id}
                                        className="relative group rounded-lg border border-border bg-white overflow-hidden"
                                        data-testid={`attachment-${att.id}`}
                                    >
                                        <a
                                            href={att.signed_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block aspect-square bg-surface-muted flex items-center justify-center"
                                        >
                                            {isImage && att.signed_url ? (
                                                <img
                                                    src={att.signed_url}
                                                    alt={att.label}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="text-muted-foreground flex flex-col items-center gap-1 p-3">
                                                    {isImage
                                                        ? <ImageIcon size={28} strokeWidth={1.4} />
                                                        : <FileText size={28} strokeWidth={1.4} />}
                                                    <span className="text-[10px] uppercase tracking-wider">
                                                        {(att.mime_type || '').split('/')[1] || 'fichier'}
                                                    </span>
                                                </div>
                                            )}
                                        </a>
                                        <div className="px-2 py-1.5 text-[10px] text-muted-foreground truncate">
                                            {att.label || '—'}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => deleteAttachment(att.id)}
                                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 backdrop-blur shadow-sm flex items-center justify-center text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                            data-testid={`attachment-delete-${att.id}`}
                                        >
                                            <X size={11} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
